import { computed, signal } from "@preact/signals-core";
import { ApiRequestError } from "../lib/api";
import { bindErrorText, bindText, html, list } from "../lib/bind";
import { formatMoney } from "../lib/money";
import { currentScope } from "../lib/scope";
import { labelRow } from "../lib/table";
import { type IncomeByClientReport, getIncomeByClientReport } from "../services/reports";
import { buildReportsSubNav } from "./reports-subnav";

interface DisplayRow {
  key: string;
  label: string;
  amount: string;
  isClientTotal: boolean;
}

function displayRows(report: IncomeByClientReport | null): DisplayRow[] {
  if (!report) return [];
  const rows: DisplayRow[] = [];
  for (const client of report.clients) {
    for (const project of client.projects) {
      rows.push({
        key: `${client.client_id}-${project.project_id ?? "none"}`,
        label: `${client.client_name} — ${project.project_name}`,
        amount: project.amount_gbp,
        isClientTotal: false,
      });
    }
    rows.push({
      key: `${client.client_id}-total`,
      label: `${client.client_name} total`,
      amount: client.amount_gbp,
      isClientTotal: true,
    });
  }
  return rows;
}

export function reportsIncomeByClientView(): Node {
  const loadError = signal("");
  const report = signal<IncomeByClientReport | null>(null);
  const fromDate = signal("");
  const toDate = signal("");

  function reload(): void {
    loadError.value = "";
    getIncomeByClientReport({ startDate: fromDate.value || undefined, endDate: toDate.value || undefined })
      .then((result) => {
        report.value = result;
        fromDate.value = result.start_date;
        toDate.value = result.end_date;
      })
      .catch((err: unknown) => {
        loadError.value = err instanceof ApiRequestError ? err.body.message : "Failed to load report";
      });
  }
  reload();

  const fragment = html`<div class="view">
    <div class="card">
      <div class="page-header"><span class="card-label">Income by Client</span></div>
      <p class="form-error"></p>
      <div class="form-row">
        <div class="form-field">
          <label for="income-from">From</label>
          <input id="income-from" type="date" />
        </div>
        <div class="form-field">
          <label for="income-to">To</label>
          <input id="income-to" type="date" />
        </div>
      </div>

      <div class="data-table income-table">
        <table>
          <thead><tr><th>Client / Project</th><th>Amount</th></tr></thead>
          <tbody class="income-body"></tbody>
        </table>
      </div>
      <table class="invoice-totals">
        <tr class="invoice-total-row"><td>Total</td><td class="income-grand-total"></td></tr>
      </table>
    </div>
  </div>`;

  const viewEl = fragment.querySelector<HTMLElement>(".view");
  if (!viewEl) throw new Error("income-by-client report view produced no .view element");
  viewEl.insertBefore(buildReportsSubNav("income-by-client"), viewEl.firstChild);

  const errorEl = fragment.querySelector<HTMLParagraphElement>(".form-error");
  const fromInput = fragment.querySelector<HTMLInputElement>("#income-from");
  const toInput = fragment.querySelector<HTMLInputElement>("#income-to");
  const incomeTable = fragment.querySelector<HTMLTableElement>(".income-table table");
  const incomeBody = fragment.querySelector<HTMLTableSectionElement>(".income-body");
  const grandTotalEl = fragment.querySelector<HTMLElement>(".income-grand-total");

  if (!errorEl || !fromInput || !toInput || !incomeTable || !incomeBody || !grandTotalEl) {
    throw new Error("income-by-client report view markup missing expected elements");
  }
  bindErrorText(errorEl, loadError);

  // The server resolves an empty range to the current accounting year and echoes it
  // back; without this the pickers stayed blank while the table showed that year's
  // figures, so the range the numbers covered was invisible. Same loop as the P/L view.
  currentScope().effect(() => {
    fromInput.value = fromDate.value;
    toInput.value = toDate.value;
  });

  fromInput.addEventListener("change", () => {
    fromDate.value = fromInput.value;
    reload();
  });
  toInput.addEventListener("change", () => {
    toDate.value = toInput.value;
    reload();
  });

  bindText(
    grandTotalEl,
    computed(() => formatMoney(report.value?.total_gbp ?? "0.00")),
  );

  list(
    incomeBody,
    computed(() => displayRows(report.value)),
    (r) => r.key,
    (r) => {
      const row = html`<tr class="${r.isClientTotal ? "income-client-total" : ""}">
        <td>${r.label}</td>
        <td>${formatMoney(r.amount)}</td>
      </tr>`.firstElementChild;
      if (!row) throw new Error("income-by-client row template produced no element");
      return labelRow(row, incomeTable);
    },
  );

  return fragment;
}
